import React, { useState, useEffect, useRef, useCallback } from "react";
import { ChatMessage } from "@/components/AI/chat/ChatMessage";
import {
  ManualAssistantMessage,
  useIsChatLoading,
} from "@/store/zustand/aiContextStore";
import { Message, useSupabaseStore } from "@/store/zustand/supabaseStore";
import { TypingIndicator } from "./TypingIndicator";
import { DiscardedMessagesSection } from "./DiscardedMessagesSection";
import { useMessageBlocks } from "./hooks/useMessageBlocks";
import { MessageRestoreStateProvider } from "./contexts/MessageRestoreStateContext";
import { usePendingQuestions } from "./contexts/PendingQuestionsContext";

export interface ChatListProps {
  messages: (Message | ManualAssistantMessage)[];
  conversationId: string;
  searchQuery?: string;
}

export const ChatList: React.FC<ChatListProps> = React.memo(
  ({ messages, conversationId, searchQuery }) => {
    const loadMoreMessages = useSupabaseStore(
      (state) => state.loadMoreMessages,
    );
    const pagination = useSupabaseStore(
      (state) => state.aiChatMessagesPagination[conversationId],
    );
    const isChatLoading = useIsChatLoading();
    const { pendingQuestions } = usePendingQuestions();
    const containerRef = useRef<HTMLDivElement>(null);
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
    const supabaseClient = useSupabaseStore((state) => state.supabaseClient);
    const [isAtScrollBottom, setIsAtScrollBottom] = useState(true);

    // Compute message blocks and restore states in a single pass (optimized)
    const { messageBlocks, getRestoreState } = useMessageBlocks(
      messages,
      isChatLoading,
    );

    // Get the last message from all blocks (for loading indicators)
    const lastMessage =
      messageBlocks.length > 0
        ? messageBlocks[messageBlocks.length - 1].messages[
            messageBlocks[messageBlocks.length - 1].messages.length - 1
          ]
        : null;

    // Reset scroll position when changing conversations
    useEffect(() => {
      if (conversationId && containerRef.current) {
        containerRef.current.scrollTop = containerRef.current.scrollHeight;
        setIsAtScrollBottom(true);
      }

      return () => {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
      };
    }, [conversationId]);

    useEffect(() => {
      if (isAtScrollBottom && containerRef.current) {
        containerRef.current.scrollTop = containerRef.current.scrollHeight;
      }
    }, [messageBlocks, isAtScrollBottom]);

    const handleScroll = useCallback(() => {
      if (!containerRef.current) return;

      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;

      // Track whether we're at the bottom of the scroll container
      const atBottom = scrollTop + clientHeight >= scrollHeight - 15;
      setIsAtScrollBottom(atBottom);

      // Only try to load more messages when:
      // 1. User is near the top of the container
      // 2. We have more messages to load
      // 3. We're not already loading
      if (scrollTop < 30 && pagination?.hasMore && !pagination?.isLoading) {
        // Debounce the load request to avoid triggering during fast scrolls
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }

        debounceTimerRef.current = setTimeout(() => {
          if (containerRef.current && containerRef.current.scrollTop < 30) {
            loadMoreMessages(conversationId, supabaseClient);
          }
        }, 150);
      }
    }, [
      pagination?.hasMore,
      pagination?.isLoading,
      conversationId,
      supabaseClient,
      loadMoreMessages,
    ]);

    if (!messages.length) {
      return null;
    }

    return (
      <MessageRestoreStateProvider getRestoreState={getRestoreState}>
        <div
          ref={containerRef}
          className="relative mx-auto max-w-2xl pl-2 pr-0 pb-12 overflow-y-scroll h-full pt-4"
          onScroll={handleScroll}
        >
          {pagination?.isLoading && (
            <div className="text-center py-1 text-secondary-n-500 text-xs">
              Loading messages...
            </div>
          )}

          {pagination?.noMoreMessages && (
            <div className="text-center py-1 text-secondary-n-500 text-xs">
              No more messages
            </div>
          )}

          {messageBlocks.map((block, blockIndex) => {
            if (block.type === "discarded") {
              return (
                <DiscardedMessagesSection
                  key={`discarded-${blockIndex}`}
                  messages={block.messages}
                  searchQuery={searchQuery}
                />
              );
            } else {
              return block.messages.map((message, msgIndex) => (
                <div key={`valid-${blockIndex}-${message.id || msgIndex}`}>
                  <ChatMessage
                    message={message as Message}
                    isLastMessage={
                      blockIndex === messageBlocks.length - 1 &&
                      msgIndex === block.messages.length - 1 &&
                      !isChatLoading
                    }
                    searchQuery={searchQuery}
                    isGreyedOut={false}
                  />
                  {msgIndex < block.messages.length - 1 && (
                    <span className="my-1" />
                  )}
                </div>
              ));
            }
          })}

          {isChatLoading &&
            (lastMessage as Message)?.role !== "assistant" &&
            !(lastMessage as ManualAssistantMessage)?.progressItems?.length && (
              <div className="pl-1 mt-2">
                <img
                  src="/icons/ai-chat-status/pending.svg"
                  className="size-4 shrink-0 flex animate-pulse"
                />
              </div>
            )}

          {/* Typing indicator or waiting for user */}
          {isChatLoading &&
            (lastMessage as Message)?.role === "assistant" &&
            (pendingQuestions ? (
              <div className="pl-1 mt-2">
                <span className="text-xs text-secondary-n-400 animate-pulse">
                  Waiting for your reply...
                </span>
              </div>
            ) : (
              <TypingIndicator />
            ))}
        </div>
      </MessageRestoreStateProvider>
    );
  },
);

ChatList.displayName = "ChatList";
